export interface BreadcrumbContext {
  url: string;
  userName: string | null;
  mode: 'single' | 'project' | null;
  projectName: string | null;
  hasConfigurations: boolean;
  line: string | null;
  roomLabel: string | null;
  productName: string | null;
  tabName: string | null;
}

/** url === null: rendered as plain text (not a link). The last crumb is the current page. */
export interface Crumb {
  label: string;
  url: string | null;
  /** Material icon name; when set the crumb renders the icon (label stays as the accessible name). */
  icon?: string;
}

const PRODUCT_ROUTE = /^\/home\/product\/(\d+)/;
const SELECT_ROUTE = /^\/home\/select\/(\d+)/;
const CHECKOUT_ROUTE = /^\/checkout\/(\d+)/;

function pathOf(url: string): string {
  return url.split(/[?#]/)[0];
}

export function productIdFromUrl(url: string): number | null {
  const path = pathOf(url);
  const match = path.match(PRODUCT_ROUTE) ?? path.match(SELECT_ROUTE) ?? path.match(CHECKOUT_ROUTE);
  return match ? Number(match[1]) : null;
}

export function buildBreadcrumbs(ctx: BreadcrumbContext): Crumb[] {
  const path = pathOf(ctx.url);
  const crumbs: Crumb[] = [];

  // Always the first crumb; the user's name lives in the account menu.
  crumbs.push({ label: 'Home', url: '/mode', icon: 'home' });
  if (path === '/mode' || path === '/' || path === '') {
    return asCurrent(crumbs);
  }

  if (ctx.mode === 'project' && ctx.projectName) {
    crumbs.push({ label: ctx.projectName, url: ctx.hasConfigurations ? '/cart' : '/lines' });
  } else if (ctx.mode === 'single') {
    crumbs.push({ label: 'Single configuration', url: null });
  }

  if (path === '/lines') {
    crumbs.push({ label: 'Choose line', url: null });
    return asCurrent(crumbs);
  }

  // The selected line is shown as the centred logo in the top bar, so the
  // trail uses a neutral "Products" step back to /home instead.
  if (path.startsWith('/home') || path.startsWith('/checkout')) {
    crumbs.push({ label: 'Products', url: '/home' });
  }

  const productName = ctx.productName ?? 'Product';
  const productLabel = ctx.roomLabel ? `${ctx.roomLabel} · ${productName}` : productName;

  const select = path.match(SELECT_ROUTE);
  const product = path.match(PRODUCT_ROUTE);
  const checkout = path.match(CHECKOUT_ROUTE);

  if (select) {
    crumbs.push({ label: productName, url: null });
  } else if (product) {
    crumbs.push({ label: productLabel, url: null });
    if (ctx.tabName) { crumbs.push({ label: ctx.tabName, url: null }); }
  } else if (checkout) {
    crumbs.push({ label: productLabel, url: `/home/product/${checkout[1]}` });
    crumbs.push({ label: 'Review', url: null });
  } else if (path.startsWith('/cart')) {
    crumbs.push({ label: 'Cart', url: null });
  } else if (path.startsWith('/order-details')) {
    crumbs.push({ label: 'Cart', url: '/cart' });
    crumbs.push({ label: 'Order', url: null });
  }

  return asCurrent(crumbs);
}

/** The last crumb is the page you are on — never a link. */
function asCurrent(crumbs: Crumb[]): Crumb[] {
  if (crumbs.length === 0) { return crumbs; }
  const last = crumbs[crumbs.length - 1];
  return [...crumbs.slice(0, -1), { ...last, url: null }];
}
